import React, { useState } from "react";
import moment from "moment";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { Tooltip } from "antd";
import { 
  RouteIcon, 
  ZapIcon, 
  ClockIcon, 
  CheckCircleIcon, 
  XCircleIcon 
} from "lucide-react";
import { 
  Collapsible, 
  CollapsibleTrigger,  
  CollapsibleContent 
} from "../ui/collapsible";
import { AgentService } from "../../services/AgentService";
import { usePermissions } from "../../PermissionsProvider";

interface RoutingResultCardProps {
  routingResult: any;
  turnIndex: number;
  totalTurns: number;
  agentId: number;
}

const RoutingResultCard: React.FC<RoutingResultCardProps> = ({
  routingResult,
  turnIndex,
  totalTurns,
  agentId,
}) => {
  const { apiKey } = usePermissions();
  const [isExpanded, setIsExpanded] = useState(false);
  const [ruleEvaluations, setRuleEvaluations] = useState<any[]>([]);
  const [loadingDetails, setLoadingDetails] = useState(false);
  
  // Always show full date for routing results
  const fullDateTime = moment(routingResult.date_created).format("MMM D, YYYY hh:mm:ss A");
  
  // Parse template to extract structure (groups, operators)
  const parseTemplate = (template: string) => {
    const structure: Array<{ index: number; groupDepth: number; operator: string | null; isGroupStart: boolean; isGroupEnd: boolean }> = [];
    
    let currentDepth = 0;
    let currentIndex = -1;
    let lastWasCondition = false;
    
    for (let i = 0; i < template.length; i++) {
      const char = template[i];
      
      if (char === "(") {
        currentDepth++;
      } else if (char === ")") {
        if (structure.length > 0) {
          structure[structure.length - 1].isGroupEnd = true;
        }
        currentDepth--;
      } else if (char === "{") {
        // Found a condition placeholder
        const match = template.substring(i).match(/^\{(\d+)\}/);
        if (match) {
          currentIndex = parseInt(match[1]);
          
          // Look ahead for operator
          const afterCondition = template.substring(i + match[0].length).trim();
          let operator = null;
          if (afterCondition.startsWith("AND") || afterCondition.startsWith("and")) {
            operator = "AND";
          } else if (afterCondition.startsWith("OR") || afterCondition.startsWith("or")) {
            operator = "OR";
          }
          
          structure.push({
            index: currentIndex,
            groupDepth: currentDepth,
            operator,
            isGroupStart: lastWasCondition === false && currentDepth > 0,
            isGroupEnd: false,
          });
          
          lastWasCondition = true;
          i += match[0].length - 1;
        }
      }
    }
    
    return structure;
  };
  
  // Render conditions with logical structure
  const renderConditionsList = (expressionTemplate: string, conditionEvals: any[]) => {
    if (!conditionEvals || conditionEvals.length === 0) {
      return <div className="text-xs text-muted-foreground">No conditions</div>;
    }
    
    const structure = expressionTemplate ? parseTemplate(expressionTemplate) : [];
    
    // Helper to format a single condition with variable value tooltips
    const formatCondition = (condition: any) => {
      const compValue = condition.comparison_value || "";
      const actualValue = condition.target_variable_value || "undefined";
      const maxLength = 15;
      const isTruncated = compValue.length > maxLength;
      const displayValue = isTruncated ? compValue.substring(0, maxLength) + "..." : compValue;
      
      // Extract metadata for semantic/classification operators
      const meta = condition.meta || {};
      const similarityScore = meta.similarity_score != null ? meta.similarity_score.toFixed(2) : null;
      const threshold = meta.threshold != null ? meta.threshold.toFixed(2) : null;
      const comparisonOp = meta.comparison_op;
      const predictedClass = meta.predicted_class;
      const confidence = meta.confidence != null ? meta.confidence.toFixed(2) : null;
      const predictedSentiment = meta.predicted_sentiment;
      
      // Wrap variable name in tooltip showing actual value
      const variableWithTooltip = (
        <Tooltip title={`${actualValue}`}>
          <span className="cursor-help underline decoration-dotted decoration-1">
            {condition.target_variable_label}
          </span>
        </Tooltip>
      );
      
      // Wrap comparison value in tooltip if truncated
      const comparisonValueElement = isTruncated ? (
        <Tooltip title={compValue}>
          <span className="cursor-help underline decoration-dotted">{displayValue}</span>
        </Tooltip>
      ) : (
        displayValue
      );
      
      let conditionElement: React.ReactNode;
      
      if (condition.operator === "sem_match" || condition.operator === "sem_similarity") {
        // Wrap operator in tooltip showing similarity score
        const operatorWithTooltip = similarityScore && threshold ? (
          <Tooltip title={`${similarityScore} ${comparisonOp || (condition.operator === "sem_match" ? ">=" : ">")} ${threshold}`}>
            <span className="cursor-help underline decoration-dotted">{condition.operator}</span>
          </Tooltip>
        ) : (
          condition.operator
        );
        conditionElement = <>{variableWithTooltip}.{operatorWithTooltip}("{comparisonValueElement}")</>;
      } else if (condition.operator === "classify" || condition.operator === "not_classify") {
        // Wrap operator in tooltip showing classification result
        const operatorLabel = condition.operator === "not_classify" ? "not_classify" : "classify";
        const operatorWithTooltip = predictedClass && confidence ? (
          <Tooltip title={`${predictedClass} - ${confidence}`}>
            <span className="cursor-help underline decoration-dotted">{operatorLabel}</span>
          </Tooltip>
        ) : (
          operatorLabel
        );
        conditionElement = <>{variableWithTooltip}.{operatorWithTooltip}("{comparisonValueElement}")</>;
      } else if (condition.operator === "sentiment") {
        // Wrap operator in tooltip showing sentiment result
        const operatorWithTooltip = predictedSentiment && confidence ? (
          <Tooltip title={`${predictedSentiment} - ${confidence}`}>
            <span className="cursor-help underline decoration-dotted">sentiment</span>
          </Tooltip>
        ) : (
          "sentiment"
        );
        conditionElement = <>{variableWithTooltip}.{operatorWithTooltip}("{comparisonValueElement}")</>;
      } else if (condition.operator === "like") {
        conditionElement = <>{variableWithTooltip}.like("{comparisonValueElement}")</>;
      } else if (condition.operator === "matches") {
        conditionElement = <>{variableWithTooltip}.matches(/{comparisonValueElement}/)</>;
      } else if (condition.operator === "equals") {
        const isBoolean = compValue === "true" || compValue === "false";
        const isNumeric = !isNaN(Number(compValue)) && compValue !== "";
        const formattedValue = (isBoolean || isNumeric) ? compValue : `"${comparisonValueElement}"`;
        conditionElement = <>{variableWithTooltip} == {formattedValue}</>;
      } else if (condition.operator === "not_equals") {
        const isBoolean = compValue === "true" || compValue === "false";
        const isNumeric = !isNaN(Number(compValue)) && compValue !== "";
        const formattedValue = (isBoolean || isNumeric) ? compValue : `"${comparisonValueElement}"`;
        conditionElement = <>{variableWithTooltip} != {formattedValue}</>;
      } else if (condition.operator === "gt") {
        conditionElement = <>{variableWithTooltip} &gt; {compValue}</>;
      } else if (condition.operator === "gte") {
        conditionElement = <>{variableWithTooltip} &gt;= {compValue}</>;
      } else if (condition.operator === "lt") {
        conditionElement = <>{variableWithTooltip} &lt; {compValue}</>;
      } else if (condition.operator === "lte") {
        conditionElement = <>{variableWithTooltip} &lt;= {compValue}</>;
      } else if (condition.operator === "is_null") {
        conditionElement = <>{variableWithTooltip} == null</>;
      } else if (condition.operator === "is_not_null") {
        conditionElement = <>{variableWithTooltip} != null</>;
      } else {
        conditionElement = <>{variableWithTooltip}.{condition.operator}({comparisonValueElement})</>;
      }
      
      return { content: conditionElement, passed: condition.passed };
    };
    
    return (
      <div className="space-y-0.5">
        {conditionEvals.map((condition: any, idx: number) => {
          const formatted = formatCondition(condition);
          const structureInfo = structure.find(s => s.index === idx) || { groupDepth: 0, operator: null as string | null, isGroupStart: false, isGroupEnd: false };
          const nextStructure = structure.find(s => s.index === idx + 1);
          
          // Check if next condition starts a new group at same or lower depth
          const showGroupSeparator = structureInfo.isGroupEnd && nextStructure && nextStructure.groupDepth <= structureInfo.groupDepth && nextStructure.isGroupStart;
          
          return (
            <React.Fragment key={idx}>
              <div 
                className="flex items-start gap-2"
                style={{ paddingLeft: `${structureInfo.groupDepth * 12}px` }}
              >
                {formatted.passed ? (
                  <CheckCircleIcon className="w-3.5 h-3.5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                ) : (
                  <XCircleIcon className="w-3.5 h-3.5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                )}
                <span 
                  className={`text-xs font-mono flex-1 ${formatted.passed ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}`}
                >
                  {formatted.content}
                  {structureInfo.operator && (
                    <span className="text-muted-foreground font-normal ml-2">{structureInfo.operator}</span>
                  )}
                </span>
              </div>
              {showGroupSeparator && (
                <div className="h-px bg-border/30 my-1" />
              )}
            </React.Fragment>
          );
        })}
      </div>
    );
  };
  
  // Load detailed routing result when expanded
  const handleExpandChange = (expanded: boolean) => {
    setIsExpanded(expanded);
    
    if (expanded && ruleEvaluations.length === 0 && !loadingDetails && routingResult.routing_mode === "rules") {
      setLoadingDetails(true);
      AgentService.getRoutingResultDetail(
        agentId,
        routingResult.id,
        () => {},
        apiKey
      ).subscribe(
        (data: any) => {
          console.log("Routing result detail data:", data);
          console.log("Rule evaluations:", data?.rule_evaluations);
          setRuleEvaluations(data?.rule_evaluations || []);
          setLoadingDetails(false);
        },
        (error: any) => {
          console.error("Error loading routing result details:", error);
          setLoadingDetails(false);
        }
      );
    }
  };
  
  return (
    <div className="relative pl-8">
      {/* Timeline dot */}
      <div className="absolute left-1.5 top-7">
        <div className="w-4 h-4 rounded-full border-2 border-background bg-purple-500 absolute transform -translate-x-1/2 -translate-y-1/2"></div>
      </div>

      {/* Timeline line */}
      {turnIndex < totalTurns - 1 && (
        <div
          className="absolute left-1.5 w-px bg-border"
          style={{
            top: "40px",
            height: "calc(100% - 60px)",
          }}
        ></div>
      )}

      <Card className="border-border bg-purple-50/30 dark:bg-purple-950/10">
        <Collapsible open={isExpanded} onOpenChange={handleExpandChange}>
          <CardContent className="p-4">
            <div className="space-y-3">
              {/* Header */}
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-2 flex-wrap flex-1 min-w-0">
                  <span className="text-xs text-muted-foreground">
                    {fullDateTime}
                  </span>

                  <RouteIcon className="w-4 h-4 text-purple-600 dark:text-purple-400" />

                  <Badge
                    variant="secondary"
                    className="text-xs bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400"
                  >
                    Routing
                  </Badge>

                  <Badge variant="outline" className="text-xs">
                    {routingResult.routing_mode === "rules" ? "Rules" : "LLM"}
                  </Badge>

                  <Badge variant="outline" className="text-xs">
                    {routingResult.evaluation_mode}
                  </Badge>
                </div>
                
                <CollapsibleTrigger className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 hover:bg-accent hover:text-accent-foreground h-7 w-7 p-0">
                  <></>
                </CollapsibleTrigger>
              </div>

              {/* Routing details - always visible */}
              <div className="space-y-2">
                <div className="text-sm">
                  <span className="text-muted-foreground">Route: </span>
                  <span className="font-gc-med text-foreground">
                    {routingResult.selected_action_name || "No match"}
                  </span>
                </div>

                {routingResult.selected_rule_label && (
                  <div className="text-sm">
                    <span className="text-muted-foreground">Selected Rule: </span>
                    <span className="font-gc-med text-foreground">{routingResult.selected_rule_label}</span>
                  </div>
                )}
                
                {routingResult.fallback_type && (
                  <div className="text-xs text-amber-600 dark:text-amber-400">
                    No match → Fallback to {routingResult.fallback_type.toUpperCase()}
                  </div>
                )}
              </div>

              {/* Metadata */}
              <div className="flex items-center gap-4 text-xs text-muted-foreground pt-2 border-t border-border/50">
                <span className="flex items-center gap-1">
                  <ClockIcon className="w-3 h-3" />
                  {routingResult.duration_ms}ms
                </span>
                {routingResult.meta?.total_rules_evaluated && (
                  <span>
                    {routingResult.meta.total_rules_evaluated} rule{routingResult.meta.total_rules_evaluated > 1 ? "s" : ""} evaluated
                  </span>
                )}
              </div>

              {/* Expandable rule evaluation details */}
              <CollapsibleContent>
                <div className="pt-4 border-t border-border">                    
                    {loadingDetails ? (
                      <div className="text-xs text-muted-foreground">Loading rule details...</div>
                    ) : ruleEvaluations.length > 0 ? (
                      <div className="space-y-3">
                        {ruleEvaluations.map((ruleEval: any) => {
                          const rule = ruleEval.rule_evaluation;
                          const conditions = ruleEval.condition_evaluations || [];
                          
                          return (
                            <div 
                              key={rule.id}
                              className="p-1.5 rounded bg-background/50 space-y-3"
                            >
                              {/* Rule header: icon, label, duration */}
                              <div className="flex items-center gap-2 text-sm border-b border-border/50 pb-2">
                                {rule.passed ? (
                                  <CheckCircleIcon className="w-4 h-4 text-green-600 dark:text-green-400 flex-shrink-0" />
                                ) : (
                                  <XCircleIcon className="w-4 h-4 text-red-600 dark:text-red-400 flex-shrink-0" />
                                )}
                                <span className="font-gc-med text-foreground">
                                  {rule.rule_label || `Rule ${rule.rule_index}`}
                                </span>
                                <span className="text-muted-foreground">·</span>
                                <span className="text-xs text-muted-foreground">
                                  {rule.evaluation_duration_ms}ms
                                </span>
                              </div>
                              
                              {/* Conditions list with pass/fail icons */}
                              <div className="pl-6">
                                {renderConditionsList(
                                  rule.condition_expression_template || rule.condition_expression || "",
                                  conditions
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : routingResult.routing_mode === "llm" ? (
                      <div className="text-xs text-muted-foreground">
                        LLM routing does not have rule-level details
                      </div>
                    ) : (
                      <div className="text-xs text-muted-foreground">
                        No rule evaluation details available
                      </div>
                    )}
                </div>
              </CollapsibleContent>
            </div>
          </CardContent>
        </Collapsible>
      </Card>
    </div>
  );
};

export default RoutingResultCard;

